import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';
import { dialogueManager } from '../game/DialogueManager.js';
import { questManager } from '../game/QuestManager.js';
import { gameState } from '../game/GameState.js';
import { DIALOGUES } from '../data/dialogue.js';

export class GardenEndingScene extends Phaser.Scene {
  constructor() {
    super('GardenEndingScene');
  }

  create() {
    gameState.currentChapter = 4;
    questManager.show(false);
    audio.playGardenSceneAudio();

    const { width, height } = this.scale;

    this.cameras.main.flash(1500, 255, 255, 255);

    // Beautiful Romantic Rose Garden Background
    this.add.image(0, 0, 'bg_garden_forever')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(0);

    // Warm golden romantic light overlay
    const romanceGlow = this.add.graphics();
    romanceGlow.fillGradientStyle(0xffd166, 0xff70a6, 0xffd166, 0xff85a1, 0.12);
    romanceGlow.fillRect(0, 0, width, height);
    romanceGlow.setDepth(1);

    this.add.particles(0, 0, 'petal_particle', {
      x: { min: 0, max: width },
      y: 0,
      lifespan: 6000,
      speedY: { min: 30, max: 60 },
      speedX: { min: -10, max: 20 },
      scale: { start: 1.2, end: 0.6 },
      alpha: { start: 0.8, end: 0.1 },
      quantity: 1,
      frequency: 180
    }).setDepth(20);

    const sunbeam = this.add.graphics();
    sunbeam.fillStyle(0xffffff, 0.12);
    sunbeam.fillTriangle(width / 2, 0, width / 2 - 200, height, width / 2 + 200, height);
    sunbeam.setDepth(2);

    // Walkway Stone Floor Alignment
    // In garden_forever.png (941h), the walkable stone path is at y=710 (710 / 941 * 540 = 408px).
    // Feet offset from center at scale 0.42 is (241 - 128) * 0.42 = 47.5px.
    const floorY = 408;
    const characterY = floorY - 48; // 360px: Feet soles rest directly on the stone walkway floor
    const talhaX = width / 2 - 55;
    const emilyX = width / 2 + 55;

    // Soft grounded contact shadows on the stone walkway directly under feet
    const shadows = this.add.graphics().setDepth(9);
    shadows.fillStyle(0x1a0f1e, 0.45);
    shadows.fillEllipse(talhaX, floorY, 40, 9);
    shadows.fillEllipse(emilyX, floorY, 40, 9);

    // Talha (2016 - 2026 Adult) standing on the left, facing RIGHT toward Emily
    this.talha = this.add.sprite(talhaX, characterY, 'talha_sheet')
      .setScale(0.42)
      .setFlipX(false) // Facing directly toward Emily
      .setDepth(10)
      .play('talha_idle');

    // Emily (2016 - 2026 Adult Labonno) standing on the right, facing LEFT toward Talha
    this.emily = this.add.sprite(emilyX, characterY, 'emily_sheet')
      .setScale(0.42)
      .setFlipX(true) // Facing directly toward Talha
      .setDepth(10)
      .play('emily_idle');

    // Shared orbit center directly between the two characters and above their heads
    this.orbitCenterX = width / 2;
    this.orbitCenterY = characterY - 96; // Safely above their heads with zero face obscuration
    this.orbitRadiusX = 118; // Wide circular path encompassing BOTH characters' heads
    this.orbitRadiusY = 26;
    this.birdOrbitAngle = 0;

    // 3 COCKATIELS CIRCLING IN CONTINUOUS SMOOTH ORBIT ABOVE BOTH CHARACTERS
    const birdGrey = this.add.sprite(this.orbitCenterX, this.orbitCenterY, 'cockatiel_grey')
      .setScale(1.25)
      .setDepth(15)
      .play('bird_grey_fly');

    const birdYellow = this.add.sprite(this.orbitCenterX, this.orbitCenterY, 'cockatiel_yellow')
      .setScale(1.25)
      .setDepth(15)
      .play('bird_yellow_fly');

    const birdCream = this.add.sprite(this.orbitCenterX, this.orbitCenterY, 'cockatiel_cream')
      .setScale(1.25)
      .setDepth(15)
      .play('bird_cream_fly');

    this.orbitBirds = [birdYellow, birdGrey, birdCream];

    // Start dialogue after players take in the beautiful garden and circling birds
    this.time.delayedCall(1600, () => {
      dialogueManager.start(DIALOGUES.level3_garden_finale, () => {
        // Keep both characters facing each other directly throughout the finale
        this.talha.setFlipX(false);
        this.emily.setFlipX(true);
        audio.playRomanticChime();
        this.showForeverEnding(width, height);
      });
    });
  }

  update(time, delta) {
    // Continuous smooth elliptical orbit of birds circling above BOTH characters together
    if (this.orbitBirds && this.orbitBirds.length > 0) {
      this.birdOrbitAngle += (delta || 16) * 0.0014; // Elegant, natural, consistent flight speed

      const count = this.orbitBirds.length;
      for (let i = 0; i < count; i++) {
        const bird = this.orbitBirds[i];
        if (!bird || !bird.active) continue;

        // 120-degree uniform spacing around the shared orbit
        const angle = this.birdOrbitAngle + (i * 2 * Math.PI / count);
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);

        // Position bird along shared elliptical orbit above both characters
        const bx = this.orbitCenterX + this.orbitRadiusX * cosA;
        const by = this.orbitCenterY + this.orbitRadiusY * sinA;

        bird.setPosition(bx, by);

        // Directional flight facing: moving rightwards (-sinA > 0) face right; moving leftwards face left
        const movingRight = -sinA > 0;
        bird.setFlipX(!movingRight);

        // 3D depth and slight perspective scaling
        if (sinA > 0) {
          bird.setDepth(16);
          bird.setScale(1.28);
        } else {
          bird.setDepth(12);
          bird.setScale(1.16);
        }
      }
    }
  }

  showForeverEnding(width, height) {
    const endContainer = this.add.container(width / 2, height * 0.35);

    const txt1 = this.add.text(0, -40, 'Together Forever.', {
      fontFamily: 'Georgia, serif',
      fontSize: '48px',
      color: '#ff70a6',
      fontStyle: 'bold',
      shadow: { color: '#ff2a75', blur: 25, fill: true }
    }).setOrigin(0.5).setAlpha(0);

    const txt2 = this.add.text(0, 20, '10 Years and Counting.', {
      fontFamily: 'Georgia, serif',
      fontSize: '32px',
      color: '#ffd166',
      shadow: { color: '#ffd166', blur: 15, fill: true }
    }).setOrigin(0.5).setAlpha(0);

    const txt3 = this.add.text(0, 70, '2016 — 2026', {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      color: '#ffffff',
      letterSpacing: 4
    }).setOrigin(0.5).setAlpha(0);

    endContainer.add([txt1, txt2, txt3]);

    this.tweens.add({ targets: txt1, alpha: 1, duration: 1500 });
    this.tweens.add({ targets: txt2, alpha: 1, delay: 1200, duration: 1500 });
    this.tweens.add({ targets: txt3, alpha: 1, delay: 2400, duration: 1500 });

    const returnBtn = this.add.text(width / 2, height - 60, '[ Return to Main Menu ]', {
      fontSize: '18px',
      color: '#ffffff',
      backgroundColor: 'rgba(50,20,60,0.8)',
      padding: { x: 16, y: 8 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setAlpha(0);

    this.tweens.add({
      targets: returnBtn,
      alpha: 1,
      delay: 4000,
      duration: 1000
    });

    returnBtn.on('pointerdown', () => {
      audio.stopGardenSceneAudio();
      this.scene.start('MainMenuScene');
    });
  }
}
